import { useState, useEffect, useCallback, useMemo } from "react";
import { Dropdown, Space, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import {
  Camera,
  ChevronDown,
  ChevronRight,
} from "feather-icons-react/build/IconComponents";
import Shelf from "./Shelf";
import "../../../styles/rack.css";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";

const Rack = ({
  id,
  number,
  name,
  shelves = [],
  outlet_id,
  cctvList = [],
  cameras = [],
  // New processor props
  p_number,
  p_ip,
  p_port,
  p_min_weight_change,
  // Read-only prop
  readOnly = false,
}) => {
  // State management
  const [isOpen, setIsOpen] = useState(false);
  const [showCameraMenu, setShowCameraMenu] = useState(false);
  const [selectedCameras, setSelectedCameras] = useState(
    cameras.map((camera) => camera.id || camera)
  );

  const { t } = useTranslation();

  // Memoized calculations for better performance
  const rackStats = useMemo(() => {
    const totalSensors = shelves.reduce(
      (count, shelf) => count + (shelf.sensors?.length || 0),
      0
    );

    const totalProducts = shelves.reduce((productCount, shelf) => {
      return (
        productCount +
        (shelf.sensors?.reduce(
          (sensorProductCount, sensor) =>
            sensorProductCount +
            (sensor.products?.reduce(
              (sum, product) => sum + (parseInt(product.quantity) || 0),
              0
            ) || 0),
          0
        ) || 0)
      );
    }, 0);

    const totalShelvesWithProducts = shelves.filter((shelf) =>
      shelf.sensors?.some((sensor) =>
        sensor.products?.some((product) => (product.quantity || 0) > 0)
      )
    ).length;

    return {
      shelves: shelves.length,
      sensors: totalSensors,
      products: totalProducts,
      activeShelvesCount: totalShelvesWithProducts,
    };
  }, [shelves]);

  // Memoized filtered cameras for performance
  const availableCameras = useMemo(() => {
    return cctvList.filter((camera) => camera && camera.id && camera.title);
  }, [cctvList]);

  const assignedCameraDetails = useMemo(() => {
    return availableCameras.filter((camera) =>
      selectedCameras.includes(camera.id)
    );
  }, [availableCameras, selectedCameras]);

  // Initialize selected cameras when cameras prop changes
  useEffect(() => {
    const cameraIds = cameras
      .map((camera) => (typeof camera === "object" ? camera.id : camera))
      .filter(Boolean);
    setSelectedCameras(cameraIds);
  }, [cameras]);

  // Handle clicks outside camera dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCameraMenu && !event.target.closest(".camera-dropdown")) {
        setShowCameraMenu(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showCameraMenu]);

  // Handle camera icon click (read-only mode)
  const handleCameraIconClick = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (!readOnly) {
        setShowCameraMenu((prev) => !prev);
      }
    },
    [readOnly]
  );

  // Toggle dropdown
  const toggleDropdown = useCallback((e) => {
    // Don't toggle if clicking on action buttons
    if (
      e.target.closest(".action-buttons") ||
      e.target.closest(".camera-dropdown") ||
      e.target.closest("button")
    ) {
      return;
    }
    setIsOpen((prev) => !prev);
  }, []);

  // Get rack number from name
  const getRackNumber = useCallback(() => {
    const match = name?.match(/^Rack (\d+)$/);
    return match ? match[1] : "";
  }, [name]);

  // Render display-only field
  const renderDisplayField = useCallback((label, value, width = "80px") => {
    const hasValue = value && value.toString().trim();

    return (
      <div
        className="d-flex flex-column"
        style={{
          minWidth: width,
          position: "relative",
          margin: "0 2px",
        }}
      >
        {/* Field Label */}
        <div
          className="field-label"
          style={{
            fontSize: "9px",
            fontWeight: "600",
            color: "#9595b5",
            textTransform: "uppercase",
            letterSpacing: "0.3px",
            marginBottom: "3px",
            textAlign: "center",
            lineHeight: "1",
          }}
        >
          {label}
        </div>

        {/* Display Mode */}
        <div
          className="display-container"
          style={{
            position: "relative",
            background: hasValue
              ? "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)"
              : "linear-gradient(135deg, #ffffff 0%, #f1f3f4 100%)",
            border: hasValue ? "1px solid #ced4da" : "1px solid #dee2e6",
            borderRadius: "5px",
            padding: "5px 8px",
            minHeight: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {/* Status Indicator */}
          <div
            className="status-indicator"
            style={{
              position: "absolute",
              top: "2px",
              right: "2px",
              width: "4px",
              height: "4px",
              borderRadius: "50%",
              backgroundColor: hasValue ? "#28a745" : "#6c757d",
            }}
          />

          {/* Value Display */}
          <div
            className="value-display"
            style={{
              fontSize: "10px",
              fontWeight: hasValue ? "600" : "400",
              color: hasValue ? "#2d3436" : "#6c757d",
              fontStyle: hasValue ? "normal" : "italic",
              textAlign: "center",
              lineHeight: "1.1",
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {hasValue ? value : "Not set"}
          </div>
        </div>
      </div>
    );
  }, []);

  return (
    <div className="rack-container-full mb-2">
      {/* Main Rack Header - Display Only */}
      <div
        className="rack-header cursor-pointer d-flex justify-content-between"
        onClick={toggleDropdown}
        style={{
          backgroundColor: "#ffffff",
          border: "1px solid #e1e5e9",
          borderRadius: "12px",
          padding: "8px 12px",
          transition: "all 0.2s ease",
          minHeight: "48px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {/* Left side - Rack Name and Stats */}
        <div className="d-flex align-items-center">
          <div className="me-3" style={{ minWidth: "215px" }}>
            <h6
              className="mb-0 d-flex align-items-center"
              style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#2c3e50",
                lineHeight: "1.2",
              }}
            >
              <span className="me-2">
                {isOpen ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                )}
              </span>
              Rack {getRackNumber()}
            </h6>
            <div
              className="text-muted"
              style={{
                fontSize: "10px",
                lineHeight: "1.1",
                marginLeft: "24px",
              }}
            >
              <span className="me-2">
                <strong>{rackStats.shelves}</strong> {t("shelf.shalves")}
              </span>
              <span className="me-2">
                <strong>{rackStats.sensors}</strong> {t("shelf.sensors")}
              </span>
              <span className="me-2">
                <strong>{rackStats.products}</strong> {t("shelf.product")}
              </span>
              {rackStats.activeShelvesCount > 0 && (
                <span className="text-success">
                  ({rackStats.activeShelvesCount} active)
                </span>
              )}
            </div>
          </div>
          <div style={{ borderLeft: "1px solid #ccc", height: "40px" }}></div>
          <div
            className="d-flex justify-content-center align-items-center gap-2 mx-3"
            style={{ flexShrink: 0 }}
          >
            {renderDisplayField("Rack #", getRackNumber(), "70px")}
            {renderDisplayField("Processor #", p_number, "90px")}
            {renderDisplayField("IP Address", p_ip, "110px")}
            {renderDisplayField("Port", p_port, "60px")}
            {renderDisplayField("Min Weight (G)", p_min_weight_change, "80px")}
          </div>
        </div>

        {/* Right side - Camera info only */}
        <div
          className="d-flex align-items-center action-buttons"
          style={{ flexShrink: 0, gap: "6px" }}
        >
          {assignedCameraDetails.length > 0 && (
            <div>
              <small className="text-muted" style={{ fontSize: "10px" }}>
                {assignedCameraDetails.map((camera) => camera.title).join(", ")}
              </small>
            </div>
          )}
        </div>
      </div>

      {/* Rack Details (Shelves) - Read-only */}
      {isOpen && (
        <div
          className="rack-details"
          style={{
            backgroundColor: "#fafbfc",
            border: "1px solid #e1e5e9",
            borderTop: "none",
            borderRadius: "0 0 12px 12px",
            padding: "12px 16px",
            maxHeight: "400px",
            overflowY: "auto",
          }}
        >
          {/* Shelves List */}
          {shelves.length === 0 ? (
            <div className="text-center py-3 text-muted">
              <div className="mb-2" style={{ fontSize: "14px" }}>
                No shelves in this rack
              </div>
              <small style={{ fontSize: "11px" }}>
                This rack has not been configured with any shelves yet
              </small>
            </div>
          ) : (
            <div style={{ maxHeight: "280px", overflowY: "auto" }}>
              {shelves.map((shelf, index) => (
                <Shelf
                  key={shelf.id || index}
                  id={shelf.id}
                  rackId={id}
                  rackName={name}
                  name={shelf.name}
                  sensors={shelf.sensors || []}
                  cameras_location={shelf.cameras_location || {}}
                  cameras={assignedCameraDetails}
                  // Make shelf read-only
                  readOnly={true}
                />
              ))}
            </div>
          )}

          {/* Display shelf count */}
          {shelves.length > 0 && (
            <div className="d-flex justify-content-end align-items-center mt-3 pt-2 border-top">
              <small className="text-muted" style={{ fontSize: "10px" }}>
                {shelves.length} shelf{shelves.length !== 1 ? "es" : ""} in this
                rack
              </small>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Rack;
